import React from 'react';
import { Box, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';

const BackgroundContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  zIndex: -1,
  overflow: 'hidden',
  background: theme.palette.mode === 'dark' 
    ? 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0f0f0f 100%)'
    : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%)',
}));

const AnimatedGradient = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  width: '200%',
  height: '200%',
  transform: 'translate(-50%, -50%)',
  background: theme.palette.mode === 'dark'
    ? 'radial-gradient(circle, rgba(102, 126, 234, 0.1) 0%, rgba(240, 147, 251, 0.05) 25%, rgba(79, 172, 254, 0.03) 50%, transparent 70%)'
    : 'radial-gradient(circle, rgba(102, 126, 234, 0.08) 0%, rgba(240, 147, 251, 0.05) 25%, rgba(79, 172, 254, 0.03) 50%, transparent 70%)',
  animation: 'rotate 20s linear infinite',
  '@keyframes rotate': {
    '0%': {
      transform: 'translate(-50%, -50%) rotate(0deg)',
    },
    '100%': {
      transform: 'translate(-50%, -50%) rotate(360deg)',
    },
  },
}));

const FloatingElement = styled(Box)(({ theme, delay = 0, size = 'medium' }) => {
  const sizes = {
    small: { width: '60px', height: '60px' },
    medium: { width: '100px', height: '100px' },
    large: { width: '150px', height: '150px' },
  };

  return {
    position: 'absolute',
    borderRadius: '50%',
    background: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(102, 126, 234, 0.05)',
    backdropFilter: 'blur(10px)',
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(102, 126, 234, 0.1)'}`,
    ...sizes[size],
    animation: `float 6s ease-in-out infinite`,
    animationDelay: `${delay}s`,
    '@keyframes float': {
      '0%, 100%': {
        transform: 'translateY(0px) rotate(0deg)',
        opacity: 0.3,
      },
      '50%': {
        transform: 'translateY(-20px) rotate(180deg)',
        opacity: 0.7,
      },
    },
  };
});

const GridPattern = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundImage: theme.palette.mode === 'dark'
    ? 'radial-gradient(circle at 1px 1px, rgba(255, 255, 255, 0.05) 1px, transparent 0)'
    : 'radial-gradient(circle at 1px 1px, rgba(102, 126, 234, 0.05) 1px, transparent 0)',
  backgroundSize: '50px 50px',
  opacity: 0.5,
}));

const NoiseOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
  opacity: theme.palette.mode === 'dark' ? 0.02 : 0.01,
  mixBlendMode: 'overlay',
}));

const ModernBackground = ({ children, showFloatingElements = true, showGrid = true }) => {
  const theme = useTheme();

  return (
    <>
      <BackgroundContainer>
        <AnimatedGradient />
        
        {showGrid && <GridPattern />}
        
        {showFloatingElements && (
          <>
            <FloatingElement
              size="large"
              delay={0}
              sx={{
                top: '10%',
                left: '10%',
                background: theme.palette.mode === 'dark'
                  ? 'rgba(102, 126, 234, 0.1)'
                  : 'rgba(102, 126, 234, 0.08)',
              }}
            />
            <FloatingElement
              size="medium"
              delay={1}
              sx={{
                top: '20%',
                right: '15%',
                background: theme.palette.mode === 'dark'
                  ? 'rgba(240, 147, 251, 0.1)'
                  : 'rgba(240, 147, 251, 0.08)',
              }}
            />
            <FloatingElement
              size="small"
              delay={2}
              sx={{
                bottom: '30%',
                left: '20%',
                background: theme.palette.mode === 'dark'
                  ? 'rgba(79, 172, 254, 0.1)'
                  : 'rgba(79, 172, 254, 0.08)',
              }}
            />
            <FloatingElement
              size="medium"
              delay={3}
              sx={{
                bottom: '15%',
                right: '25%',
                background: theme.palette.mode === 'dark'
                  ? 'rgba(250, 112, 154, 0.1)'
                  : 'rgba(250, 112, 154, 0.08)',
              }}
            />
            <FloatingElement
              size="small"
              delay={4}
              sx={{
                top: '60%',
                left: '50%',
                background: theme.palette.mode === 'dark'
                  ? 'rgba(168, 237, 234, 0.1)'
                  : 'rgba(168, 237, 234, 0.08)',
              }}
            />
          </>
        )}
        
        <NoiseOverlay />
      </BackgroundContainer>
      {children}
    </>
  );
};

export default ModernBackground; 